import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, ButtonSwitchGroup, useTheme } from '@ublend-npm/aulaui-core';
import setHomeViewRole from '@app/actions/home/setHomeViewRole.action';
import {
  HOME_VIEW_ROLE_LABELS,
  HOME_VIEW_ROLES,
  HomeViewRole,
} from '@app/constants/home';
import { getCanSwitchHomeViewRole, getHomeViewRole } from '@app/selectors/home';

const TOGGLE_HEIGHT = 32;
const TOGGLE_WIDTH = 138;
const SELECTED_INSET = 4;
const BUTTON_PADDING = '4px 8px';
const SEGMENT_COUNT = HOME_VIEW_ROLES.length;
const SWITCH_EASING = 'cubic-bezier(0.4, 0, 0.2, 1)';
const SWITCH_DURATION = '0.35s';

const EducatorStudentToggle = () => {
  const dispatch = useDispatch();
  const activeRole = useSelector(getHomeViewRole);
  const canSwitch = useSelector(getCanSwitchHomeViewRole);
  const { typography } = useTheme();

  if (!canSwitch) {
    return null;
  }

  const handleSelect = (role: HomeViewRole) => {
    if (role !== activeRole) {
      dispatch(setHomeViewRole(role));
    }
  };
  const activeIndex = Math.max(HOME_VIEW_ROLES.indexOf(activeRole), 0);
  const isEducatorSelected = activeIndex === 0;

  return (
    <Box
      id="home-switchrole-widget"
      data-widget-id="home-switchrole-widget"
      data-tour="home-switchrole"
      sx={{
        width: TOGGLE_WIDTH,
        // Override ButtonSwitchGroup track + sliding pill sizing.
        '& > .MuiBox-root': {
          width: '100%',
          minHeight: TOGGLE_HEIGHT,
          height: TOGGLE_HEIGHT,
          padding: 0,
          overflow: 'hidden',
          boxSizing: 'border-box',
          // Keep translateX slide (animatable). left stays fixed at 4px;
          // width = half - inset so Student ends 4px from the right edge.
          '& > .MuiBox-root': {
            top: `${SELECTED_INSET}px !important`,
            left: `${SELECTED_INSET}px !important`,
            right: 'auto !important',
            height: `calc(100% - ${SELECTED_INSET * 2}px) !important`,
            width: `calc(100% / ${SEGMENT_COUNT} - ${SELECTED_INSET}px) !important`,
            transition: `transform ${SWITCH_DURATION} ${SWITCH_EASING} !important`,
            willChange: 'transform',
          },
        },
        '& .MuiButton-root': {
          flex: '1 1 0',
          minWidth: 0,
          width: `calc(100% / ${SEGMENT_COUNT})`,
          minHeight: TOGGLE_HEIGHT,
          height: TOGGLE_HEIGHT,
          margin: '0 !important',
          boxSizing: 'border-box',
          whiteSpace: 'nowrap',
          overflow: 'visible',
          textOverflow: 'clip',
          fontFamily: typography.fontFamily,
          fontWeight: typography.fontWeightMedium,
          fontSize: '0.8125rem',
          lineHeight: '22px',
          letterSpacing: 0,
          textTransform: 'none',
          transition: `color ${SWITCH_DURATION} ${SWITCH_EASING}, padding ${SWITCH_DURATION} ${SWITCH_EASING} !important`,
          // Center selected text inside the inset pill (4px outer inset).
          '&:first-of-type': {
            padding: isEducatorSelected
              ? `4px 8px 4px ${8 + SELECTED_INSET}px !important`
              : `${BUTTON_PADDING} !important`,
          },
          '&:last-of-type': {
            padding: isEducatorSelected
              ? `${BUTTON_PADDING} !important`
              : `4px ${8 + SELECTED_INSET}px 4px 8px !important`,
          },
        },
      }}
    >
      <ButtonSwitchGroup
        size="medium"
        options={HOME_VIEW_ROLES.map((role) => HOME_VIEW_ROLE_LABELS[role])}
        activeIndex={activeIndex}
        onSelect={(index) => handleSelect(HOME_VIEW_ROLES[index])}
      />
    </Box>
  );
};

export default EducatorStudentToggle;
